/** @format */
import {
    Tooltip,
    TooltipAlignment,
    TooltipVariant,
} from '@mpb-com/feather/components/atoms/tooltip';
import { debounce } from 'lodash';
import React, {
    FocusEvent,
    JSXElementConstructor,
    MouseEvent,
    MutableRefObject,
    ReactNode,
    useEffect,
    useRef,
    useState,
} from 'react';

// eslint-disable-next-line boundaries/element-types -- should be using the feather version
import { useMeasure } from '@toucan/client/shared/hooks/use-measure';
// eslint-disable-next-line boundaries/element-types -- should be using the feather version
import { usePortal } from '@toucan/client/shared/hooks/use-portal';
// eslint-disable-next-line boundaries/element-types -- should be using the feather version
import { useWindowResizeCallback } from '@toucan/client/shared/hooks/use-window-resize';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Legacy code: do not use as an example.
interface TooltipPortalProps {
    children: ReactNode;
    className?: string;
    variant?: TooltipVariant;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Legacy code: do not use as an example.
export interface TooltipBind<E = HTMLElement> {
    ref: MutableRefObject<E | null>;
    onMouseEnter: (e: MouseEvent) => void;
    onMouseLeave: (e: MouseEvent) => void;
    onFocus: (e: FocusEvent) => void;
    onBlur: (e: FocusEvent) => void;
    'aria-describedby'?: string;
}

type UseTooltipReturn<E> = [
    TooltipBind<E>,
    JSXElementConstructor<TooltipPortalProps>,
    () => void,
    () => void,
];

const MAX_TOOLTIP_WIDTH = 75;
const PAGE_GUTTER = 1.2;

/**
 * @deprecated use @mpb-com/feather/hooks/use-tooltip
 */
export const useTooltip = <E extends Element>(args: {
    /**
     * A unique id used to let accessibility tools know about the tooltip
     */
    id: string;

    /**
     * If the open and close methods should be called to open the tooltip
     *
     * @default false
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    manual?: boolean;

    /**
     * Triggers closing mechanism when user clicks away from the tooltip
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    closeOnClickAway?: boolean;

    /**
     * If the close method should be called when clicking away from the tooltip
     *
     * @default false
     */
    onClickAway?: () => void;
}): UseTooltipReturn<E> => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const { id, manual = false, closeOnClickAway = false, onClickAway } = args;
    const triggerRef = useRef<E | null>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const tooltipHover = useRef<boolean>(false);
    const triggerHover = useRef<boolean>(false);
    const manualOpen = useRef<boolean>(false);
    const [portalStyles, setPortalStyles] = useState({});

    const [internalTriggerBounds, setInternalTriggerBounds] =
        useState<DOMRect>();
    const [tooltipAlignment, setTooltipAlignment] = useState<TooltipAlignment>(
        TooltipAlignment.LEFT
    );

    const { Portal, isOpen, openPortal, closePortal } = usePortal({
        bindTo: typeof window !== 'undefined' ? document.body : null,
        onOpen: handlePortalOpen,
        onClose: handlePortalClose,
    });

    const bounds = useMeasure(tooltipRef);

    /**
     * Updates the position of the Portal to be in the correct place for the Tooltip
     *
     * @param tooltipRef The ref of the portal
     */
    function updatePortalPosition(ref: MutableRefObject<HTMLElement | null>) {
        if (ref.current === null) {
            return;
        }

        if (!triggerRef.current) {
            console.warn('useTooltip: Cannot measure trigger');
            return;
        }

        if (!ref.current) {
            console.warn('useTooltip: Cannot measure portal');
            return;
        }

        const portal = ref.current;

        const triggerBounds = triggerRef.current.getBoundingClientRect();
        const tooltipBounds = portal.getBoundingClientRect();

        const maxLeftAlignedWidth = Math.min(
            MAX_TOOLTIP_WIDTH,
            (window.innerWidth - triggerBounds.left) / 10 - PAGE_GUTTER
        );
        const maxRightAlignedWidth = Math.min(
            MAX_TOOLTIP_WIDTH,
            (triggerBounds.right - PAGE_GUTTER) / 10
        );

        const isLeftAligned = !(
            tooltipBounds.width / 10 > maxLeftAlignedWidth &&
            maxRightAlignedWidth > maxLeftAlignedWidth
        );

        const maxWidth = isLeftAligned
            ? `${maxLeftAlignedWidth}rem`
            : `${maxRightAlignedWidth}rem`;

        const translateX = isLeftAligned
            ? `translateX(${triggerBounds.left}px)`
            : `translateX(${triggerBounds.right}px) translateX(-100%)`;

        const translateY = `translateY(${
            triggerBounds.bottom + window.pageYOffset
        }px)`;

        setPortalStyles({
            position: 'absolute',
            zIndex: '20',
            top: '0',
            left: '0',
            textAlign: isLeftAligned ? 'left' : 'right',
            maxWidth,
            transform: `${translateX} ${translateY}`,
        });
        setInternalTriggerBounds(triggerBounds);
        setTooltipAlignment(
            isLeftAligned ? TooltipAlignment.LEFT : TooltipAlignment.RIGHT
        );
    }

    function handlePortalOpen() {
        updatePortalPosition(tooltipRef);
    }

    function handlePortalClose() {
        triggerHover.current = false;
        tooltipHover.current = false;
        manualOpen.current = false;
    }

    /**
     * Works out if the portal should be closed yet and if so, closes it
     */
    function possiblyClosePortal() {
        if (
            document.activeElement === triggerRef.current ||
            tooltipHover.current ||
            triggerHover.current ||
            manualOpen.current
        ) {
            return;
        }

        closePortal();
    }

    // Debouncing so all events fire before deciding if the tooltip should be open or not
    const possiblyClosePortalDebounced = useRef(
        debounce(possiblyClosePortal, 50)
    );

    function handleTooltipMouseEnter() {
        if (manual) {
            return;
        }

        tooltipHover.current = true;

        if (!isOpen) {
            openPortal();
        }
    }

    function handleTooltipMouseLeave() {
        tooltipHover.current = false;
        if (!manual) {
            manualOpen.current = false;
        }
        possiblyClosePortalDebounced.current();
    }

    /**
     * Opens the portal when the trigger is hovered over
     */
    function handleMouseEnter() {
        if (manual) {
            return;
        }

        triggerHover.current = true;

        if (!isOpen) {
            openPortal();
        }
    }

    /**
     * Closes the portal when the trigger is no longer being hovered over
     */
    function handleMouseLeave() {
        triggerHover.current = false;
        if (!manual) {
            manualOpen.current = false;
        }
        possiblyClosePortalDebounced.current();
    }

    /**
     * Opens the portal when the trigger is focused
     */
    function handleFocus() {
        if (manual) {
            return;
        }

        if (!isOpen) {
            openPortal();
        }
    }

    /**
     * Closes the portal when the trigger is blurred
     */
    function handleBlur() {
        if (!manual) {
            manualOpen.current = false;
        }
        possiblyClosePortalDebounced.current();
    }

    function handleClickAway() {
        if (manual && isOpen) {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions, @typescript-eslint/prefer-optional-chain -- Legacy code: do not use as an example.
            onClickAway && onClickAway();
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- Legacy code: do not use as an example.
            closeOnClickAway && close();
        }
    }

    /**
     * Opens the Tooltip
     */
    function open() {
        manualOpen.current = true;

        if (!isOpen) {
            openPortal();
        }
    }

    /**
     * Closes the tooltip
     */
    function close() {
        manualOpen.current = false;
        triggerHover.current = false;
        tooltipHover.current = false;

        if (document.activeElement === triggerRef.current) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
            (triggerRef.current as any).blur();
        }

        if (isOpen) {
            closePortal();
        }
    }

    const TooltipWrapper = ({
        children,
        className,
        variant,
    }: TooltipPortalProps) => {
        return (
            <Portal>
                <Tooltip
                    id={`tooltip-${id}`}
                    onClickAway={handleClickAway}
                    onMouseEnter={handleTooltipMouseEnter}
                    onMouseLeave={handleTooltipMouseLeave}
                    triggerBounds={internalTriggerBounds}
                    alignment={tooltipAlignment}
                    data-testid="use-tooltip__tooltip"
                    className={className}
                    variant={variant}
                    ref={tooltipRef}
                    $portalStyles={portalStyles}
                >
                    {children}
                </Tooltip>
            </Portal>
        );
    };

    // Props that should be bound to the trigger element
    const bind = {
        ref: triggerRef,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        onFocus: handleFocus,
        onBlur: handleBlur,
        'aria-describedby': isOpen ? `tooltip-${id}` : undefined,
    };

    useWindowResizeCallback(() => {
        updatePortalPosition(tooltipRef);
    });

    useEffect(() => {
        updatePortalPosition(tooltipRef);
    }, [bounds]);

    return [bind, TooltipWrapper, open, close];
};
